import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AdminApiService } from '../../services/admin-api.service';
import { RecruitmentApiService } from '../../services/recruitment-api.service';

@Component({
  selector: 'app-invite-user-modal',
  templateUrl: './invite-user-modal.component.html',
  standalone: false
})
export class InviteUserModalComponent {
  inviteForm: FormGroup;
  submitting = false;
  errorMessage = '';
  invitedRecruiterUserId: string | null = null;

  roles = ['member', 'admin', 'manager', 'recruiter'];

  constructor(
    private fb: FormBuilder,
    private adminApi: AdminApiService,
    private recruitmentApi: RecruitmentApiService,
    public dialogRef: MatDialogRef<InviteUserModalComponent>
  ) {
    this.inviteForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName:  ['', Validators.required],
      email:     ['', [Validators.required, Validators.email]],
      role:      ['member', Validators.required],
      organizationName: ['']
    });

    this.inviteForm.get('role')?.valueChanges.subscribe((role) => {
      const organizationName = this.inviteForm.get('organizationName');
      if (role === 'recruiter') {
        organizationName?.setValidators([Validators.required]);
      } else {
        organizationName?.clearValidators();
        organizationName?.setValue('');
        this.invitedRecruiterUserId = null;
      }
      organizationName?.updateValueAndValidity();
    });
  }

  submit(): void {
    if (this.inviteForm.invalid) return;

    this.submitting = true;
    this.errorMessage = '';

    if (this.invitedRecruiterUserId) {
      this.provisionRecruiterOrganization(this.invitedRecruiterUserId);
      return;
    }

    const { firstName, lastName, email, role } = this.inviteForm.value;
    this.adminApi.inviteUser({ firstName, lastName, email, role }).subscribe({
      next: (res) => {
        if (res.success && role === 'recruiter') {
          this.invitedRecruiterUserId = res.user?.userId || null;
          if (!this.invitedRecruiterUserId) {
            this.submitting = false;
            this.errorMessage = 'Recruiter was invited, but the returned user ID was missing.';
            return;
          }
          this.provisionRecruiterOrganization(this.invitedRecruiterUserId);
        } else if (res.success) {
          this.submitting = false;
          this.dialogRef.close({ success: true });
        } else {
          this.submitting = false;
          this.errorMessage = res.message || 'Failed to invite user.';
        }
      },
      error: (err) => {
        console.error('Invite error', err);
        this.submitting = false;
        this.errorMessage = 'Something went wrong. Please try again.';
      }
    });
  }

  private provisionRecruiterOrganization(recruiterUserId: string): void {
    const organizationName = this.inviteForm.get('organizationName')?.value?.trim();
    this.recruitmentApi.provisionRecruiterOrganization(recruiterUserId, organizationName).subscribe({
      next: (organization) => {
        this.submitting = false;
        this.dialogRef.close({ success: true, organization });
      },
      error: (err) => {
        console.error('Organization provisioning error', err);
        this.submitting = false;
        this.errorMessage = 'Recruiter invited, but organization setup failed. Fix the issue and retry setup.';
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
